/**
 * NIGHTBOUND - stores / inventory.
 *
 * Grouped by category. Each entry shows what the item does; tools show the
 * passive bonus they grant while owned.
 */

import { useEffect, useMemo, useState } from 'react'
import type { ItemCategory, ItemDefinition } from '../../shared/items'
import { ITEM_CATEGORIES } from '../../shared/items'
import { useGame } from '../state/GameProvider'
import { ItemIcon } from '../components/ItemIcon'
import { Button, Chip, Panel, PanelHeading, cx } from '../components/ui'

const CATEGORY_LABELS: Record<ItemCategory, string> = {
  FOOD: 'Food',
  CARE: 'Care',
  TOY: 'Toys',
  GEAR: 'Gear',
  MATERIAL: 'Materials',
}

const RARITY_TONE: Record<ItemDefinition['rarity'], 'neutral' | 'good' | 'warn' | 'bad'> = {
  COMMON: 'neutral',
  UNCOMMON: 'good',
  RARE: 'warn',
  CORRUPT: 'bad',
}

export function InventoryScreen() {
  const { inventory, refreshInventory, creature } = useGame()
  const [selected, setSelected] = useState<string | null>(null)

  useEffect(() => {
    void refreshInventory()
  }, [refreshInventory])

  const grouped = useMemo(() => {
    const map = new Map<ItemCategory, typeof inventory>()
    for (const category of ITEM_CATEGORIES) map.set(category, [])
    for (const entry of inventory) {
      map.get(entry.item.category)?.push(entry)
    }
    return map
  }, [inventory])

  const selectedEntry = inventory.find((entry) => entry.item.key === selected) ?? null

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
      <header className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-bone-100">Stores</h1>
          <p className="mt-1 text-xs text-ash-400">
            Everything you have scavenged. {creature ? `${creature.name} depends on it.` : ''}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void refreshInventory()}>
          Refresh
        </Button>
      </header>

      <div className="grid gap-5 lg:grid-cols-[1fr_18rem]">
        <div className="space-y-4">
          {inventory.length === 0 ? (
            <Panel className="p-6 text-sm text-ash-400">
              Nothing in the stores yet.
            </Panel>
          ) : (
            ITEM_CATEGORIES.map((category) => {
              const entries = grouped.get(category) ?? []
              if (entries.length === 0) return null
              return (
                <Panel key={category} className="overflow-hidden">
                  <PanelHeading right={<span className="nb-label text-[8px]">{entries.length}</span>}>
                    {CATEGORY_LABELS[category]}
                  </PanelHeading>
                  <ul className="divide-y divide-ash-700/30">
                    {entries.map((entry) => (
                      <li key={entry.item.key}>
                        <button
                          type="button"
                          onClick={() => setSelected(entry.item.key)}
                          aria-pressed={selected === entry.item.key}
                          className={cx(
                            'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-night-800/60',
                            selected === entry.item.key && 'bg-night-800/70',
                          )}
                        >
                          <ItemIcon itemKey={entry.item.key} glyph={entry.item.glyph} size="md" />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2">
                              <span className="text-sm text-bone-200">{entry.item.name}</span>
                              {!entry.item.consumable ? <Chip tone="neutral">tool</Chip> : null}
                            </span>
                            <span className="mt-0.5 block truncate text-[11px] text-ash-400">
                              {entry.item.description}
                            </span>
                          </span>
                          <span className="nb-numeric shrink-0 text-sm text-bone-300">
                            {entry.item.consumable ? `×${entry.quantity}` : '—'}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </Panel>
              )
            })
          )}
        </div>

        <aside className="lg:sticky lg:top-20 lg:self-start">
          <Panel className="overflow-hidden">
            <PanelHeading>Detail</PanelHeading>
            {selectedEntry ? (
              <div className="space-y-3 p-4">
                <div className="flex items-center gap-3">
                  <ItemIcon itemKey={selectedEntry.item.key} glyph={selectedEntry.item.glyph} size="lg" />
                  <div>
                    <div className="font-display text-lg text-bone-100">{selectedEntry.item.name}</div>
                    <div className="mt-1 flex gap-1">
                      <Chip tone={RARITY_TONE[selectedEntry.item.rarity]}>
                        {selectedEntry.item.rarity.toLowerCase()}
                      </Chip>
                      <Chip tone="neutral">{CATEGORY_LABELS[selectedEntry.item.category]}</Chip>
                    </div>
                  </div>
                </div>
                <p className="text-xs leading-relaxed text-bone-300/90">
                  {selectedEntry.item.description}
                </p>
                <p className="font-display text-[11px] italic leading-relaxed text-ash-400">
                  {selectedEntry.item.flavour}
                </p>

                {selectedEntry.item.effects ? (
                  <div className="space-y-1.5 border-t border-ash-700/40 pt-3">
                    <div className="nb-label text-[9px]">On use</div>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(selectedEntry.item.effects).map(([key, value]) => (
                        <Chip key={key} tone={value >= 0 ? 'good' : 'bad'}>
                          {value > 0 ? '+' : ''}
                          {value} {key}
                        </Chip>
                      ))}
                    </div>
                  </div>
                ) : null}

                {selectedEntry.item.toolBonus ? (
                  <div className="space-y-1.5 border-t border-ash-700/40 pt-3">
                    <div className="nb-label text-[9px]">While owned</div>
                    <p className="text-[11px] leading-snug text-ash-300">
                      {selectedEntry.item.toolBonus.note}
                    </p>
                  </div>
                ) : null}

                <div className="border-t border-ash-700/40 pt-3 text-[11px] text-ash-400">
                  {selectedEntry.item.consumable ? (
                    <p>You hold {selectedEntry.quantity}. It is used up.</p>
                  ) : (
                    <p>A tool. Never consumed.</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="p-4 text-xs text-ash-400">
                Choose something from the stores to inspect it.
              </p>
            )}
          </Panel>
        </aside>
      </div>
    </div>
  )
}
